import { nextTick, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

interface ScrollOptions {
  behavior?: 'auto' | 'smooth'
  threshold?: number
}

export const useChatScroll = (
  scrollContainerRef: Ref<HTMLElement | null>,
  isLoading: Ref<boolean>,
) => {
  const scrollHandlerAdded = ref(false)

  const getScrollElement = (): HTMLElement | null => {
    if (!scrollContainerRef.value) return null
    try {
      return scrollContainerRef.value
    } catch (error) {
      debugConsoleError(error)
      return null
    }
  }

  const addScrollHandler = (handler: (event: Event) => void) => {
    const scrollEl = getScrollElement()
    if (scrollEl && !scrollHandlerAdded.value) {
      scrollEl.addEventListener('scroll', handler, { passive: true })
      scrollHandlerAdded.value = true
    }
  }

  const removeScrollHandler = (handler: (event: Event) => void) => {
    const scrollEl = getScrollElement()
    if (scrollEl && scrollHandlerAdded.value) {
      scrollEl.removeEventListener('scroll', handler)
      scrollHandlerAdded.value = false
    }
  }

  const isNearBottom = (threshold = 100): boolean => {
    const scrollEl = getScrollElement()
    if (!scrollEl) return false
    const { scrollTop, scrollHeight, clientHeight } = scrollEl
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    return distanceFromBottom <= threshold
  }

  const scrollToBottom = (options: ScrollOptions = {}) => {
    const { behavior = 'smooth' } = options
    const scrollEl = getScrollElement()
    if (!scrollEl) return

    try {
      if (behavior === 'auto') {
        scrollEl.classList.add('chat-scrolling')
      }

      const performScroll = (attempt = 0) => {
        const scrollHeight = scrollEl.scrollHeight
        const clientHeight = scrollEl.clientHeight
        const maxScrollTop = scrollHeight - clientHeight

        scrollEl.scrollTop = maxScrollTop

        setTimeout(() => {
          const actualScrollHeight = scrollEl.scrollHeight
          const actualMaxScrollTop = actualScrollHeight - scrollEl.clientHeight

          if (Math.abs(actualScrollHeight - scrollHeight) > 10) {
            scrollEl.scrollTop = actualMaxScrollTop
          }

          if (behavior === 'auto') {
            setTimeout(() => {
              scrollEl.classList.remove('chat-scrolling')
            }, 50)
          }
        }, 100)
      }

      requestAnimationFrame(() => performScroll(0))
    } catch (error) {
      debugConsoleError(error)
      const scrollEl = getScrollElement()
      if (scrollEl) scrollEl.classList.remove('chat-scrolling')
    }
  }

  watch(isLoading, loading => {
    if (loading) {
      let intervalId: ReturnType<typeof setInterval> | null = null

      const keepScrollAtBottom = () => {
        if (!isLoading.value) {
          if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
          }
          return
        }

        const scrollEl = getScrollElement()
        if (scrollEl) {
          scrollEl.style.scrollBehavior = 'auto'
          scrollEl.scrollTop = scrollEl.scrollHeight
        }
      }

      nextTick(() => {
        keepScrollAtBottom()
        intervalId = setInterval(keepScrollAtBottom, 16)
      })
    }
  })

  return {
    getScrollElement,
    addScrollHandler,
    removeScrollHandler,
    scrollToBottom,
    isNearBottom,
  }
}
